import { useMemo } from 'react';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
import { ErrorBoundary } from '@sparknz/journeys-common';
import {
  Box,
  Breadcrumb,
  Breadcrumbs,
  Text,
} from '@sparknz/spark-pattern-library';
import { LinkRegion, useTrackPageView } from '@sparknz/tracking';

import { DeviceGalleryQuery$data } from '__generated__/DeviceGalleryQuery.graphql';
import { HeaderContentQuery$data } from '__generated__/HeaderContentQuery.graphql';
import { LEGACY_SHOP_ROUTE } from 'shared/constants';
import { usePageTransition } from 'shared/hooks/usePageTransition';
import { useSegment } from 'shared/hooks/useSegment';
import GlobalNavsLayout from 'shared/layouts/GlobalNavsLayout';
import { useIsWebView } from 'shared/providers/isWebView';
import { getPath } from 'shared/utils';
import { SectionContainer } from './components';
import DeviceGalleryBanner from './components/DeviceGalleryBanner';
import DeviceGallery from './DeviceGallery';
import DeviceGalleryError from './DeviceGalleryError';
import DeviceGalleryLoader from './DeviceGalleryLoader';
import { getSortQuery } from './utils/getSortQuery';

const CategoryTitle = styled(Text)`
  ${({ theme: { media, spacing } }) => css`
    padding-top: ${spacing.s1_5};

    ${media.up('slim')} {
      padding-top: ${spacing.s3};
    }

    ${media.up('big')} {
      padding-top: ${spacing.s5};
    }
  `}
`;

interface DeviceGalleryInternalPageProps {
  deviceGallery: DeviceGalleryQuery$data['deviceGallery'];
}

export function DeviceGalleryPageInternal({
  deviceGallery,
}: DeviceGalleryInternalPageProps) {
  const { filters, products, selectedSubcategoryId, subcategories } =
    deviceGallery;
  const isWebView = useIsWebView();
  const { push, query: routerQuery, isReady } = useRouter();
  const { isBusiness, channel } = useSegment();
  const { isTransitioning } = usePageTransition(/^(?!.*products).*$/);

  const handleSubcategoryChange = (subcategoryId: string) => {
    const pathname = getPath(`/${subcategoryId}`, channel);
    const query = getSortQuery(routerQuery);

    push({ pathname, query });
  };

  const categoryName = useMemo(
    () =>
      subcategories.find(
        ({ categoryId }) => categoryId === selectedSubcategoryId,
      )?.name,
    [selectedSubcategoryId],
  );

  useTrackPageView({
    pageName: `shop.${selectedSubcategoryId}GalleryState`,
    breadcrumbs: ['home', 'shop', selectedSubcategoryId],
    primaryCategory: 'shop',
    pageType: 'Landing Page',
    pageTitle: `${selectedSubcategoryId} Gallery`,
    isVisibleInNav: true,
    subCategories: ['mobile plans'],
  });

  return (
    <>
      {isReady && !isBusiness ? (
        <ErrorBoundary fallback={() => null}>
          <DeviceGalleryBanner />
        </ErrorBoundary>
      ) : null}
      {isTransitioning ? (
        <DeviceGalleryLoader />
      ) : (
        <>
          <SectionContainer>
            <Box pt={['s2', 's3']}>
              <LinkRegion regionId="breadcrumbs">
                {isWebView ? null : (
                  <Breadcrumbs>
                    <Breadcrumb href="/">Home</Breadcrumb>
                    <Breadcrumb href={LEGACY_SHOP_ROUTE}>Shop</Breadcrumb>
                    <Breadcrumb href="#" isLastBreadcrumb>
                      {categoryName}
                    </Breadcrumb>
                  </Breadcrumbs>
                )}
              </LinkRegion>
            </Box>
            <CategoryTitle variant="headingM" mb="s5">
              {categoryName}
            </CategoryTitle>
          </SectionContainer>
          <DeviceGallery
            filters={filters}
            products={products}
            onSelectSubcategoryId={handleSubcategoryChange}
            selectedSubcategoryId={selectedSubcategoryId}
            subcategories={subcategories}
          />
        </>
      )}
    </>
  );
}

interface DeviceGalleryPageProps extends DeviceGalleryInternalPageProps {
  headerContent?: HeaderContentQuery$data;
}

export default function DeviceGalleryPage({
  headerContent,
  deviceGallery,
}: DeviceGalleryPageProps) {
  return (
    <GlobalNavsLayout ssr headerContent={headerContent}>
      <ErrorBoundary fallback={DeviceGalleryError}>
        <DeviceGalleryPageInternal deviceGallery={deviceGallery} />
      </ErrorBoundary>
    </GlobalNavsLayout>
  );
}
